<script setup lang="ts">
import logo from '@/assets/img/logo.png';
import { useFilterUser } from '@/store/filterUser';
import logout from '@/assets/img/nav/ic-logout-icon.svg';
import { useAuthStore } from '@/modules/auth/store/auth';
import { storeToRefs } from 'pinia';
import { AppFormCard, AppTypography } from './base';
import NavbarMenu from './NavbarMenu.vue';
import NavbarAddressSelector from './address/NavbarAddressSelector.vue';
import { useNuxtApp } from '#app';
import { inject } from 'vue';

const { resetFilterOptions } = useFilterUser();
const { resetOptions, user } = useAuthStore();
const { hasLoggedInUser } = storeToRefs(useAuthStore());

const { signOut } = useAuth();

const handleLogout = async () => {
  await resetFilterOptions();
  await resetOptions();
  await signOut({ redirect: false });
  window.location.href = '/login';
};

const handleAddressSelected = (address: any) => {
  console.log('Address selected in navbar:', address);
  
  // Emit event to parent or use a simple global approach
  window.dispatchEvent(new CustomEvent('address-changed', { detail: address }));
};
</script>

<template>
  <v-container max-width="264">
    <AppFormCard>
      <!-- Logo Section - Sticky -->
      <div class="logo-section d-flex justify-space-between">
        <div class="logo-container">
          <img :src="logo" class="app-logo" />
          <AppTypography class="app-name">SupportApp</AppTypography>
        </div>
        <div class="app-name-container">
          <AppTypography class="app-name">{{ user?.user_type === 'worker' ? 'Support Worker' : 'Support Seeker' }}</AppTypography>
        </div>
      </div>

      <!-- Address Selector - Sticky -->
      <div class="address-section">
        <NavbarAddressSelector @address-selected="handleAddressSelected" />
      </div>

      <!-- Scrollable Content Container -->
      <div class="scrollable-content">
        <!-- Navigation Menu -->
        <div class="navigation-section">
          <NavbarMenu>
            <!-- <template #default="{ isComingSoon }">
              <div v-if="isComingSoon" class="upcoming-chip-container">
                <v-chip size="small" color="primary" class="upcoming-chip">
                  <AppTypography type="caption-1">Upcoming</AppTypography>
                </v-chip>
              </div>
            </template> -->
            <template #prepend="{ nav, isActive }">
              <div class="nav-icon-container">
                <img :src="isActive(nav) ? nav?.activeIcon : nav?.icon" width="24" height="24" />
              </div>
            </template>
          </NavbarMenu>
        </div>

        <!-- PWA Install Button Section -->
        <!-- <div class="pwa-install-section">
          <PwaInstallButton 
            button-text="Download App"
            button-color="primary"
            button-variant="elevated"
            button-size="small"
            button-icon="mdi-download"
            button-class="pwa-install-btn"
          />
        </div> -->
      </div>

      <!-- Logout Section - Always at bottom -->
      <div class="logout-section">
        <v-list class="logout-list">
          <v-list-item @click="handleLogout" class="logout-item">
            <template v-slot:prepend>
              <div class="logout-icon-container">
                <img :src="logout" width="24" height="24" />
              </div>
            </template>
            <AppTypography type="body-2" v-if="hasLoggedInUser" class="logout-text">
              Logout
            </AppTypography>
          </v-list-item>
        </v-list>
      </div>
    </AppFormCard>
  </v-container>
</template>

<style lang="scss" scoped>
// Container Styles
.v-container {
  max-width: 260px;
  position: fixed;
  z-index: 1000;
  display: none;
  padding: 0 !important; // Remove all padding like Vuexy
  margin: 0 !important; // Remove all margins
  left: 0 !important; // Stick to left edge
  top: 0 !important; // Stick to top edge

  @media (min-width: 768px) {
    display: unset;
  }
}

// Card Container Styles
.v-card {
  padding: 0 !important;
  height: 100vh !important; // Full height like Vuexy
  border-radius: 0 !important; // No border radius like Vuexy
  box-shadow: 0 2px 10px rgba(58, 53, 65, 0.1) !important;
  border: none !important; // No border like Vuexy
  background: #ffffff;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  :deep(.v-card-text) {
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 0 !important;
    overflow: hidden;
  }
}

// Logo and Brand Section Styles
.logo-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px 20px 20px 20px; // Increased top padding
  border-bottom: 1px solid rgba(76, 78, 100, 0.12);
  flex-shrink: 0;
  
  .logo-container {
    display: flex;
    align-items: center;
    gap: 12px; // Increased gap from 8px to 12px
    margin-bottom: 8px; // Add space between logo/title and user type
    
    .app-logo {
      width: 50px !important; // Reduced from 28px to 24px
      height: 50px !important; // Reduced from 28px to 24px
      flex-shrink: 0;
    }
    
    .app-name {
      font-size: 24px !important; // Increased from 18px to 20px
      font-weight: 700;
      color: #8C47F6;
      line-height: 1.2;
    }
  }
  
  .user-type-container {
    display: flex;
    justify-content: center;
    width: 100%;
    
    .user-type {
      font-size: 14px !important;
      font-weight: 500;
      color: rgba(76, 78, 100, 0.68);
      line-height: 1.2;
      text-align: center;
    }
  }
}

.app-name-container {
  display: flex;
  justify-content: start;
  width: 100%;
  margin-left: 48px;
}

.app-name {
      font-size: 14px !important; // Increased from 18px to 20px
      font-weight: 700;
      color: #8C47F6;
      line-height: 1.2;
    }

// Address Section Styles - Fixed below logo
.address-section {
  padding: 12px 20px; // Reduced padding
  border-bottom: 1px solid rgba(76, 78, 100, 0.12);
  position: sticky;
  top: 60px; // Adjusted for reduced logo section
  z-index: 9;
  background: #ffffff;
}

// Scrollable content container
.scrollable-content {
  flex: 1;
  overflow-y: auto !important;
  overflow-x: hidden;
  min-height: 0;
  
  // Custom scrollbar
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(165, 163, 174, 0.2);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(165, 163, 174, 0.4);
    border-radius: 3px;
    
    &:hover {
      background: rgba(165, 163, 174, 0.6);
    }
  }
}

// Navigation Section Styles - Reduced top padding
.navigation-section {
  padding: 4px 0 20px 0; // Reduced top padding from 12px to 4px
  
  .nav-icon-container {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px; // Back to original size
    height: 24px;
  }
}

// List Item Styles - Match Vuexy spacing
:deep(.v-list-item) {
  color: rgb(var(--v-theme-on-surface), 0.8) !important; // Match service location label color
  border-radius: 8px !important;
  margin: 4px 20px !important; // Increased horizontal margin like Vuexy
  padding: 12px 8px !important; // Increased padding like Vuexy
  min-height: 48px; // Increased height like Vuexy
  transition: all 0.25s ease;
  display: flex !important;
  align-items: center !important;
  position: relative;
  font-weight: 500 !important; // Medium weight like content

  &.v-list-item--disabled {
    opacity: 0.4 !important;
    color: rgb(var(--v-theme-on-surface), 0.4) !important; // Lighter version for disabled
    font-weight: 500 !important;
  }

  // Icon alignment - absolute positioning for perfect alignment
  .v-list-item__prepend {
    position: absolute !important;
    left: 8px !important; // Adjusted for new padding
    top: 50% !important;
    transform: translateY(-50%) !important;
    margin: 0 !important;
    width: 24px !important;
    height: 24px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    
    .nav-icon-container {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
    }
  }

  // Text content - aligned with absolute positioning
  .v-list-item__content {
    margin-left: 44px !important; // Adjusted for new icon position
    padding: 0 !important;
    display: flex !important;
    flex-direction: column !important;
    align-items: flex-start !important;
    width: 100%;
  }

  // Upcoming chip container styling
  .upcoming-chip-container {
    margin-top: 4px;
    width: 100%;
    display: flex;
    justify-content: flex-start;
  }

  .v-chip {
    border-radius: 6px !important;
    padding: 0px 8px !important;
    text-transform: uppercase;
    font-weight: 500 !important;
    font-size: 10px !important;
    letter-spacing: 0.4px;

    &.v-chip--size-small {
      min-height: 20px !important;
      height: 20px !important;
    }
  }

  &:hover {
    background-color: rgba(115, 103, 240, 0.04) !important;
    color: rgb(var(--v-theme-on-surface)) !important; // Fully dark on hover, matching service location text
    transform: translateX(2px);
    
    // Keep consistent styling on hover for disabled items
    &.v-list-item--disabled {
      color: rgb(var(--v-theme-on-surface), 0.5) !important; // Match service location disabled color
      font-weight: 500 !important;
    }
  }

  &.v-list-item--active {
    background: rgba(var(--v-theme-primary), 0.1) !important; // Match address selector background
    border: 1px solid rgba(var(--v-theme-outline), 0.2) !important; // Match address selector border
    color: #8C47F6 !important; // Dark purple text
    border-radius: 8px !important;
    box-shadow: none !important; // Remove shadow to match address selector

    .v-list-item__overlay {
      opacity: 0 !important;
    }

    p {
      color: #8C47F6 !important; // Dark purple text
      font-weight: 600 !important;
      line-height: 20px !important;
      text-align: left;
    }
    
    // Hover effect for active item - match address selector hover
    &:hover {
      background: rgba(var(--v-theme-primary), 0.15) !important;
      border-color: rgba(var(--v-theme-primary), 0.3) !important;
    }
  }
  
  // Typography - Match content styling
  .v-list-item-title {
    font-size: 15px !important;
    font-weight: 500 !important;
    line-height: 20px !important;
    letter-spacing: 0.15px;
    color: inherit !important;
  }
  
  // Ensure AppTypography inherits the content-matching styling
  p {
    color: inherit !important;
    font-weight: 500 !important;
    font-size: 15px !important;
    
    // Keep consistent weight when parent is disabled
    .v-list-item--disabled & {
      font-weight: 500 !important;
    }
  }
}

// Logout Section Styles - Match navigation alignment and spacing
.logout-section {
  border-top: 1px solid rgba(76, 78, 100, 0.12);
  padding: 16px 0 24px 0; // Increased padding to match navigation
  flex-shrink: 0;
  
  .logout-list {
    padding: 0 !important;
    background: transparent;
  }
  
  .logout-item {
    border-radius: 8px !important;
    margin: 4px 20px !important; // Match navigation margins
    padding: 12px 8px !important; // Match navigation padding
    min-height: 48px; // Match navigation height
    transition: all 0.25s ease;
    background-color: rgba(255, 62, 29, 0.08) !important;
    display: flex !important;
    align-items: center !important;
    position: relative;
    
    // Logout icon alignment - match navigation
    .v-list-item__prepend {
      position: absolute !important;
      left: 8px !important; // Match navigation icon position
      top: 50% !important;
      transform: translateY(-50%) !important;
      margin: 0 !important;
      width: 24px !important;
      height: 24px !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
    }
    
    &:hover {
      background-color: rgba(255, 62, 29, 0.12) !important;
      transform: translateX(2px);
      
      .logout-text {
        color: #e53e3e !important; // Darker red for visibility
      }
    }
  }
  
  .logout-icon-container {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    background-color: transparent !important;
  }
  
  .logout-text {
    margin-left: 44px !important; // Match navigation text positioning
    font-weight: 500 !important; // Medium weight for visibility
    font-size: 14px !important;
    color: #e53e3e !important; // Darker red for visibility
    transition: color 0.25s ease;
    letter-spacing: 0.1px;
  }
}

// PWA Install Section Styles
.pwa-install-section {
  padding: 20px;
  border-top: 1px solid rgba(76, 78, 100, 0.12);
  flex-shrink: 0;
  
  .pwa-install-btn {
    width: 100% !important;
    height: 40px !important;
    font-size: 14px !important;
    font-weight: 500 !important;
    text-transform: none !important;
    border-radius: 8px !important;
    box-shadow: 0 2px 8px rgba(140, 71, 246, 0.2) !important;
    transition: all 0.25s ease !important;
    
    &:hover {
      transform: translateY(-1px) !important;
      box-shadow: 0 4px 12px rgba(140, 71, 246, 0.3) !important;
    }
    
    &:active {
      transform: translateY(0) !important;
    }
  }
}

// Responsive Design
@media (max-width: 1024px) {
  .v-container {
    max-width: 260px;
    padding: 10px !important;
  }
}

// Dark Mode Support
.v-theme--dark {
  .v-card {
    background: #2f3349 !important;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15) !important;
    border-color: rgba(255, 255, 255, 0.12);
  }
  
  .logo-section,
  .address-section,
  .logout-section {
    border-color: rgba(255, 255, 255, 0.12);
    background: #2f3349;
  }
  
  .app-name {
    color: rgba(255, 255, 255, 0.87);
  }
  
  .user-type {
    color: rgba(255, 255, 255, 0.68) !important;
  }
  
  :deep(.v-list-item) {
    color: rgba(255, 255, 255, 0.68) !important;
    
    &:hover {
      background-color: rgba(115, 103, 240, 0.08) !important;
      color: rgba(255, 255, 255, 0.87) !important;
    }
    
    &.v-list-item--active {
      color: #ffffff !important;
      
      p {
        color: #ffffff !important;
      }
    }
  }
}
</style>
